import type { CustomCompType } from '@neo/blocks/forms/types'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { FileAudioIcon, UploadIcon, XIcon } from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { errorToast } from 'v2/utils/toasts'
import type { VoiceCloneFormValues } from '../custom-voice.types'

interface AudioFileDisplayProps {
  file: File
  onRemove: () => void
}

const AudioFileDisplay = ({ file, onRemove }: AudioFileDisplayProps) => (
  <div
    className="flex items-center justify-between p-3 bg-background rounded-md border"
    data-testid="audio-file-display"
  >
    <div className="flex items-center gap-2 text-secondary">
      <FileAudioIcon className="w-4 h-4 text-secondary" />
      <span className="text-xs-500 truncate max-w-[200px]" title={file.name}>
        {file.name}
      </span>
      <span className="text-xs-400">{(file.size / 1024).toFixed(2)} KB</span>
    </div>
    <Tooltip content="Remove file">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        onClick={onRemove}
        data-testid="button-remove-audio-file"
      >
        <XIcon className="w-4 h-4" />
      </Button>
    </Tooltip>
  </div>
)

interface AudioUploadDropzoneProps {
  onDrop: (files: File[]) => void
  hasFile: boolean
}

const AudioUploadDropzone = ({ onDrop, hasFile }: AudioUploadDropzoneProps) => {
  const acceptedTypes = {
    'audio/mpeg': ['.mp3'],
    'audio/mp3': ['.mp3'],
    'audio/wav': ['.wav'],
    'audio/wave': ['.wav'],
    'audio/x-wav': ['.wav'],
    'audio/x-m4a': ['.m4a'],
    'audio/mp4': ['.m4a'],
    'audio/m4a': ['.m4a'],
    'audio/aac': ['.m4a', '.aac'],
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: (rejections) => {
      errorToast({
        title: 'Unable to select file',
        description:
          rejections[0]?.errors?.[0]?.message || 'Please check the file type and size (max 10MB)',
      })
    },
    accept: acceptedTypes,
    disabled: hasFile,
    maxSize: MAX_AUDIO_SIZE_BYTES,
    multiple: false,
  })

  const getStatusText = () => {
    if (hasFile) return 'File selected'
    if (isDragActive) return 'Drop audio file here...'
    return 'Drag audio file or click to upload'
  }

  if (hasFile) return null

  return (
    <div
      {...getRootProps()}
      className={cn(
        'border-2 border-dashed border-border rounded-lg p-4 text-center cursor-pointer transition-colors',
        isDragActive ? 'border-primary bg-primary/5' : 'hover:border-primary',
      )}
      data-testid="dropzone-audio-upload"
    >
      <input {...getInputProps()} data-testid="input-audio-upload" />
      <div className="flex gap-2 text-tertiary items-center">
        <UploadIcon className="size-5" />
        <div className="text-left">
          <div className="text-xs-500">{getStatusText()}</div>
          <div className="text-xs-400">
            Audio formats (MP3, WAV, M4A) up to 10MB, minimum 10 seconds
          </div>
        </div>
      </div>
    </div>
  )
}

const MIN_AUDIO_DURATION_SECONDS = 10
const MAX_AUDIO_SIZE_BYTES = 10 * 1024 * 1024 // 10MB

const getAudioDuration = async (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async (event) => {
      const audioContext = new AudioContext()
      try {
        const arrayBuffer = event.target?.result as ArrayBuffer
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        resolve(audioBuffer.duration)
      } catch {
        reject(new Error('Failed to decode audio file'))
      } finally {
        await audioContext.close()
      }
    }
    reader.onerror = () => reject(new Error('Failed to read audio file'))
    reader.readAsArrayBuffer(file)
  })
}

export const AudioUploadField: CustomCompType<VoiceCloneFormValues> = ({ value, onChange }) => {
  const audioFile = value as File | null

  const handleDrop = async (files: File[]) => {
    if (files?.length === 0) return

    const file = files[0]

    try {
      const duration = await getAudioDuration(file)
      if (duration < MIN_AUDIO_DURATION_SECONDS) {
        errorToast({
          title: 'Audio too short',
          description: `Audio must be at least ${MIN_AUDIO_DURATION_SECONDS} seconds. Your file is ${duration.toFixed(1)} seconds.`,
        })
        return
      }
      onChange(file)
    } catch {
      errorToast({
        title: 'Invalid audio file',
        description: 'Unable to read audio duration. Please try a different file.',
      })
    }
  }

  const handleRemove = () => {
    onChange(null)
  }

  return (
    <div className="space-y-2">
      <div className="text-s-500">Audio Sample</div>
      <AudioUploadDropzone onDrop={handleDrop} hasFile={!!audioFile} />
      {audioFile && <AudioFileDisplay file={audioFile} onRemove={handleRemove} />}
    </div>
  )
}
